import { Component, Element, Event, EventEmitter, Fragment, h, Host, Prop, State, Watch } from "@stencil/core";

import {
  StzhDropdownChangeEvent,
  StzhDropdownOption,
  StzhSearchChipsResult,
  StzhSearchOverlayCloseClickEvent,
  StzhSearchResult,
} from "../../../index";

import axios from "axios";
import { hasSlot, raf } from "../../../utils/utils";

import { StzhSearchLocalizedText } from "./stzh-search.localization";

import { addMediaChangeListener, media, removeMediaChangeListener } from "../../../utils/media-utils";

import debounce from "lodash/debounce";

function defaultTemplateResult(search: HTMLStzhSearchElement, item: StzhSearchResult): string {
  return `
    <stzh-card-${search.variant === "teaser" ? "superteaser" : "searchresult"}
      href="{href}"
      dateline="{dateline}"
      tag="{tag}"
      topic="{topic}"
      service="{service}"
      event-location="{eventLocation}"
      hyphens-enabled="${this.hyphensEnabled}"
      ${item.eventDateStart && 'event-date-start="{eventDateStart}"'}
      ${item.eventDateEnd && 'event-date-end="{eventDateEnd}"'}
      download-heading="{downloadHeading}"
      meta='{meta}'
      breadcrumb-items='{breadcrumbItems}'
      download-meta='{downloadMeta}'
      datalist-items='{datalistItems}'
      ${item.imagePosition && 'image-position="{imagePosition}"'}
      ${item.download && 'download="{download}"'}
      style="
        ${item.serviceBackgroundColor ? "--service-background-color: {serviceBackgroundColor};" : ""}
        ${item.serviceColor ? "--service-color: {serviceColor};" : ""}
      "
    >
      <div slot="heading">{heading}</div>
      <div slot="description">{description}</div>
      ${
        item.image
          ? item.imagePosition === "right"
            ? `<img src="${item.image}" slot="image" />`
            : `<stzh-ratio ${
                search.variant === "teaser" ? 'ratio="2:1"' : 'ratio="21:9" ratio-small="1:1"'
              } slot="image"><img src="${item.image}" alt=""/></stzh-ratio>`
          : ""
      }
    </stzh-card-${search.variant === "teaser" ? "superteaser" : "searchresult"}>
  `;
}

function defaultResultWrapperProps(search: HTMLStzhSearchElement) {
  return {
    layout: search.variant === "teaser" ? "teasers" : "searchresults",
  } as HTMLStzhCardlistElement;
}

/**
 * @slot - Slot for any content
 * @slot filters-main - Slot for main filters
 * @slot filters-aside - Slot for aside filters
 */
@Component({
  tag: "stzh-search",
  styleUrl: "stzh-search.scss",
})
export class StzhSearch {
  /** Translation strings */
  @Prop() localization: StzhSearchLocalizedText;

  /** Variant */
  @Prop() variant: "default" | "teaser" = "default";

  /** Whether search results should be automatically fetched when a main filters has changed. */
  @Prop() autofetchMainFilters: boolean = false;

  /** Whether search results should be automatically fetched when a aside filters has changed. */
  @Prop() autofetchAsideFilters: boolean = false;

  /** Whether to hide the main filters slot */
  @Prop({ reflect: true }) hideMainFilters: boolean = false;

  /** Whether to hide the aside filters slot */
  @Prop({ reflect: true }) hideAsideFilters: boolean = false;

  /** Whether to hide the results heading */
  @Prop({ reflect: true }) hideResultsHeading: boolean = false;

  /** Sort by initial value */
  @Prop({ mutable: true }) sortBy: string;
  private _sortBy: string;

  /** Sort options */
  @Prop({ mutable: true }) sortOptions: StzhDropdownOption[] | string = [];
  private _sortOptions: StzhDropdownOption[] = [];

  /** API URL */
  @Prop() api: string = "";

  /** Debounce time in ms */
  @Prop() debounceTime: number = 500;

  /** Numbers of results shown with each load more action */
  @Prop() limit: number = 6;

  /** Whether search request should be fetched initially with set filters */
  @Prop() initialFetch: boolean = false;

  /** Whether url query should be updated when filter has changed */
  @Prop() preventUpdateUrl: boolean = false;

  /** Automatic Hyphens */
  @Prop({ reflect: true }) hyphensEnabled: boolean = false;

  /** Will be overwritten by api result (`templates.result` or `results[x].html`) */
  @Prop() templateResult: string | ((search: HTMLStzhSearchElement, item: StzhSearchResult) => string) =
    defaultTemplateResult;

  /** Element used for list wrapper element */
  @Prop() templateResultWrapperElement: string | ((search: HTMLStzhSearchElement) => string) = function () {
    return "stzh-cardlist";
  };

  /** Properties applied to list wrapper element */
  @Prop() templateResultWrapperProps:
    | { [key: string]: any }
    | ((search: HTMLStzhSearchElement) => { [key: string]: any }) = defaultResultWrapperProps;

  /** Heading level of results heading */
  @Prop() resultHeadingLevel: "1" | "2" | "3" | "4" = "3";

  /** Empty text */
  @Prop() emptyText: string;

  /** Empty text */
  @Prop() heading: string;

  /** Empty link href */
  @Prop() emptyLinkHref: string;

  /** Empty link label */
  @Prop() emptyLinkLabel: string;

  /** Label for the subscribe button */
  @Prop() subscribeLabel: string = "Subscribe";

  /** Path which is used as a base for the subscribe button. If not provided, the button won't be rendered. */
  @Prop() subscribePath: string;

  /** Query parameters which are being added to the subscribe button path, in addition to the form values. */
  @Prop() subscribeAdditionalQueryParameters: { [key: string]: string }[] | string;

  @Element() element: HTMLStzhSearchElement;

  @Watch("totalCount")
  @Watch("results")
  totalCountWatcher() {
    this._showMoreLink = this.totalCount > this.results.length;
  }

  @Watch("preventUpdateUrl")
  preventUpdateUrlWatcher() {
    this.updateParams();
  }

  @Watch("sortOptions")
  sortOptionsWatcher(newValue: StzhDropdownOption[] | string) {
    if (typeof newValue === "string") {
      this._sortOptions = JSON.parse(newValue);
    } else {
      this._sortOptions = newValue;
    }
  }

  @Watch("sortBy")
  sortByWatcher(newValue: string | undefined) {
    if (typeof newValue === "string") {
      this._sortBy = newValue;
    } else {
      this._sortBy = undefined;
    }
  }

  @State() totalCount: number = 0;
  private _showMoreLink: boolean = false;

  @State() loading: boolean = false;
  @State() error: string = "";

  @State() results: StzhSearchResult[] = [];
  @State() chipResultsHeading: string;
  @State() chipResults: StzhSearchChipsResult[] = [];
  @State() resultsHeading: string;
  @State() resultsText: string;
  @State() hasRequiredParams: boolean = false;
  @State() firstFetched: boolean = false;
  @State() initiallyFetched: boolean = false;
  @State() isDetailsOpen: boolean = false;
  @State() isStzhAnchornavVisible: boolean = false;

  /** Search overlay close click event */
  @Event() stzhOverlayCloseClick: EventEmitter<StzhSearchOverlayCloseClickEvent>;

  private currentController: AbortController;
  private params: URLSearchParams;
  private formElement: HTMLFormElement;

  private renderResultTemplate(html: string, item: StzhSearchResult) {
    let template =
      html ||
      (typeof this.templateResult === "function" ? this.templateResult(this.element, item) : this.templateResult);

    [
      "href",
      "heading",
      "name",
      "additionalInformation",
      "numbers",
      "dateline",
      "description",
      "image",
      "imagePosition",
      "tag",
      "topic",
      "service",
      "serviceColor",
      "serviceBackgroundColor",
      "meta",
      "breadcrumbItems",
      "eventLocation",
      "eventDateStart",
      "eventDateEnd",
      "download",
      "downloadHeading",
      "downloadMeta",
      "datalistItems",
    ].forEach(property => {
      const regex = new RegExp(`\{${property}\}`, "gi");
      let value = item[property] || "";

      if (["meta", "breadcrumbItems", "downloadMeta", "datalistItems", "numbers"].includes(property) && value) {
        value = JSON.stringify(value);
      }

      template = template.replace(regex, value);
    });

    return template;
  }

  private async updateParams(initial: boolean = false) {
    const updateByFormElements = () => {
      const formData = new FormData(this.formElement);
      // @ts-ignore
      this.params = new URLSearchParams(formData);
      this.params.set("variant", this.variant);
    };

    if (initial) {
      const initialParams = new URLSearchParams(location.search);
      let search = initialParams.get("search");
      const q = initialParams.get("q");
      const query = initialParams.get("query");
      if (!search) {
        if (query) {
          search = `query=${query}`;
        } else if (q) {
          search = `q=${q}`;
        }
      }

      if (search) {
        this.initialFetch = true;
        const currentSearchParams = new URLSearchParams(search);

        [...new Set(currentSearchParams.keys())].forEach(key => {
          const values = currentSearchParams.getAll(key);

          // value as string/number: input, ... / pagination
          const valueInputs = Array.from(
            document.querySelectorAll(
              `:where(stzh-input,stzh-radiogroup,stzh-chipselect,stzh-amount,stzh-datepicker,stzh-monthyearpicker,stzh-timepicker,stzh-pagination,stzh-toggle)[name="${key}"]`,
            ),
          ) as
            | HTMLStzhInputElement[]
            | HTMLStzhRadiogroupElement[]
            | HTMLStzhChipselectElement[]
            | HTMLStzhAmountElement[]
            | HTMLStzhDatepickerElement[]
            | HTMLStzhMonthyearpickerElement[]
            | HTMLStzhTimepickerElement[]
            | HTMLStzhPaginationElement[]
            | HTMLStzhToggleElement[];

          // value as array: checkboxgroup
          const valueArrayInputs = Array.from(
            document.querySelectorAll(`:where(stzh-checkboxgroup)[name="${key}"]`),
          ) as HTMLStzhCheckboxgroupElement[];

          // items as array: dropdown
          const itemsArrayInputs = Array.from(
            document.querySelectorAll(`:where(stzh-dropdown)[name="${key}"]`),
          ) as HTMLStzhDropdownElement[];

          valueInputs.forEach(
            (
              input:
                | HTMLStzhInputElement
                | HTMLStzhRadiogroupElement
                | HTMLStzhChipselectElement
                | HTMLStzhAmountElement
                | HTMLStzhDatepickerElement
                | HTMLStzhMonthyearpickerElement
                | HTMLStzhTimepickerElement
                | HTMLStzhPaginationElement
                | HTMLStzhToggleElement,
            ) => {
              input.value = values[0];
            },
          );

          valueArrayInputs.forEach((input: HTMLStzhCheckboxgroupElement) => {
            input.value = values;
          });

          itemsArrayInputs.forEach((input: HTMLStzhDropdownElement) => {
            input.items = values;
          });
        });
      }
    }

    await raf();
    updateByFormElements();

    let hasRequiredParams = true;

    const requiredInputFields = Array.from(
      this.element.querySelectorAll(":where(stzh-input)[required]"),
    ) as HTMLStzhInputElement[];

    const requiredDropdownFields = Array.from(
      this.element.querySelectorAll(":where(stzh-dropdown)[required]"),
    ) as HTMLStzhDropdownElement[];

    const requiredMonthyearpickers = Array.from(
      this.element.querySelectorAll(":where(stzh-monthyearpicker)[required]"),
    ) as HTMLStzhMonthyearpickerElement[];

    const requiredFields = Array.from(
      this.element.querySelectorAll(
        ":where(stzh-amount,stzh-radiogroup,stzh-chipselect,stzh-datepicker,stzh-monthyearpicker,stzh-timepicker,stzh-pagination,stzh-toggle)[required]",
      ),
    ) as
      | HTMLStzhAmountElement[]
      | HTMLStzhRadiogroupElement[]
      | HTMLStzhChipselectElement[]
      | HTMLStzhDatepickerElement[]
      | HTMLStzhTimepickerElement[]
      | HTMLStzhPaginationElement[]
      | HTMLStzhToggleElement[];

    const requiredCheckboxFields = Array.from(
      this.element.querySelectorAll(":where(stzh-checkbox)[required]"),
    ) as HTMLStzhCheckboxElement[];

    requiredCheckboxFields.forEach((field: HTMLStzhCheckboxElement) => {
      const invalid = !field.checked;

      if (!initial) {
        field.invalid = invalid;
      }

      if (invalid) {
        hasRequiredParams = false;
      }
    });

    requiredInputFields.forEach((field: HTMLStzhInputElement) => {
      const invalid =
        field.value === "" ||
        (field.minlength && field.minlength > field.value.length) ||
        (field.maxlength && field.maxlength < field.value.length);

      if (!initial) {
        field.invalid = invalid;
      }

      if (invalid) {
        hasRequiredParams = false;
      }
    });

    requiredDropdownFields.forEach((field: HTMLStzhDropdownElement) => {
      const invalid = field.items.length === 0;

      if (!initial) {
        field.invalid = invalid;
      }

      if (invalid) {
        hasRequiredParams = false;
      }
    });

    requiredFields.forEach(
      (
        field:
          | HTMLStzhAmountElement
          | HTMLStzhRadiogroupElement
          | HTMLStzhCheckboxElement
          | HTMLStzhChipselectElement
          | HTMLStzhDatepickerElement
          | HTMLStzhTimepickerElement
          | HTMLStzhPaginationElement
          | HTMLStzhToggleElement,
      ) => {
        const invalid = field.value.toString().length === 0;

        if (!initial) {
          field.invalid = invalid;
        }

        if (invalid) {
          hasRequiredParams = false;
        }
      },
    );

    requiredMonthyearpickers.forEach((field: HTMLStzhMonthyearpickerElement) => {
      const invalid = field.value.toString().length === 0;

      if (!initial) {
        field.invalidYear = invalid;
        field.invalidMonth = invalid;
      }

      if (invalid) {
        hasRequiredParams = false;
      }
    });

    this.hasRequiredParams = hasRequiredParams;
  }

  private fetch = async (append: boolean = false, initial: boolean = false) => {
    this.loading = true;
    this.error = "";

    if (this.currentController) {
      this.currentController.abort();
    }

    if (!this.hasRequiredParams && !append) {
      this.loading = false;
      return;
    }

    let apiUrl = this.api.replace(/\{lang\}/gi, this.localization.$locale);
    let limit = this.limit.toString();

    this.params.set("offset", append ? this.results.length.toString() : "0");

    if (initial) {
      const search = new URLSearchParams(location.search).get("search");

      if (search) {
        const urlLimit = new URLSearchParams(search).get("limit");

        if (urlLimit) {
          limit = urlLimit;
        }
      }
    }

    this.params.set("limit", limit);

    this.currentController = new AbortController();

    let data: {
      error?: boolean;
      message?: string;
      templates?: {
        result: string;
      };
      meta?: {
        total: number;
      };
      resultsHeading?: string;
      resultsText?: string;
      results?: StzhSearchResult[];
      chipResultsHeading?: string;
      chipResults?: StzhSearchChipsResult[];
    } = {};

    try {
      // load data from API
      const response = await axios(apiUrl, {
        params: this.params,
        signal: this.currentController.signal,
      });

      data = response.data;
    } catch (error) {
      this.error = this.localization.error;
    }

    this.resultsHeading = data.resultsHeading || "";
    this.resultsText = data.resultsText || "";
    this.totalCount = data.meta?.total || 0;

    if (data.error) {
      this.error = data.message || this.localization.error;
    }

    if (data.chipResults && data.chipResults.length) {
      this.chipResultsHeading = data.chipResultsHeading ?? "";
      this.chipResults = data.chipResults;
    } else {
      this.chipResultsHeading = "";
      this.chipResults = [];
    }

    if (data.results) {
      if (append) {
        this.results = [
          ...this.results,
          ...data.results.map(item => ({
            ...item,
            html: this.renderResultTemplate(item.html || data.templates?.result, item),
          })),
        ];
      } else {
        this.results = data.results.map(item => ({
          ...item,
          html: this.renderResultTemplate(item.html || data.templates?.result, item),
        }));
      }
    }

    if (!initial && !this.preventUpdateUrl) {
      const newUrlParams = new URLSearchParams(location.search);
      const searchParams = new URLSearchParams(this.params);

      searchParams.delete("offset");
      searchParams.set("limit", this.results.length.toString());
      newUrlParams.set("search", searchParams.toString());

      window.history.replaceState(null, "", `${location.pathname}?${newUrlParams}`);
    }

    if (initial || !this.firstFetched) {
      this.loading = false;
    } else {
      // to be able to see loading effect we delay turning off loading state
      window.setTimeout(() => {
        this.loading = false;
      }, this.debounceTime);
    }

    this.firstFetched = true;
  };

  private debouncedFetch = debounce(this.fetch, this.debounceTime);

  private onDropdownSortChange = async (event: CustomEvent<StzhDropdownChangeEvent>) => {
    const target: HTMLStzhDropdownElement = event.target as HTMLStzhDropdownElement;
    const sortBy = (await target.getValue()) as string;
    if (sortBy) {
      this._sortBy = sortBy;
    }
    await this.updateParams();
    this.debouncedFetch();
  };

  private handleMainFilterChange = async () => {
    if (this.autofetchMainFilters) {
      await this.updateParams();
      this.debouncedFetch();
    }
  };

  private handleAsideFilterChange = async () => {
    if (this.autofetchAsideFilters) {
      await this.updateParams();
      this.debouncedFetch();
    }
  };

  private handleFormSubmit = async (event: Event) => {
    event.preventDefault();

    await this.updateParams();
    this.fetch();
  };

  private handleMoreClick = () => {
    this.fetch(true);
  };

  private handleMediaChange = () => {
    this.isDetailsOpen = media("large").matches;
  };

  private handleFormReset = async () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(async () => {
        await this.updateParams();
      });
    });
  };

  async componentDidLoad() {
    this.isStzhAnchornavVisible =
      document.documentElement.style.getPropertyValue("--stzh-anchornav-is-visible") === "1";

    // update params initially to get hidden filters
    // and set initialFetch to true if search params exist
    await this.updateParams(true);

    if (this.initialFetch) {
      await this.fetch(false, true);
      this.initiallyFetched = true;
    } else {
      this.initiallyFetched = true;
    }
  }

  async componentWillLoad() {
    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "search");
    }
    this.isStzhAnchornavVisible =
      document.documentElement.style.getPropertyValue("--stzh-anchornav-is-visible") === "1";
    this.sortOptionsWatcher(this.sortOptions);
    this.sortByWatcher(this.sortBy);
  }

  connectedCallback() {
    addMediaChangeListener(this.handleMediaChange);
    this.handleMediaChange();
  }

  disconnectedCallback() {
    removeMediaChangeListener(this.handleMediaChange);
  }

  private renderResultsText(): DocumentFragment {
    return <stzh-text class="stzh-search__results-text" color="primary" innerHTML={this.resultsText}></stzh-text>;
  }

  private renderSubscribeButton(): DocumentFragment {
    const params = new URLSearchParams();
    let subscribeAdditionalQueryParameters = this.subscribeAdditionalQueryParameters;
    if (typeof this.subscribeAdditionalQueryParameters === "string") {
      try {
        subscribeAdditionalQueryParameters = JSON.parse(this.subscribeAdditionalQueryParameters);
      } catch (noJson) {}
    }
    for (const entry of subscribeAdditionalQueryParameters) {
      for (const [key, value] of Object.entries(entry)) {
        if (value) {
          params.set(key, value);
        }
      }
    }
    const currentQueryParameters = new URLSearchParams(window.location.search);
    const search = currentQueryParameters.get("search");
    if (search) {
      const currentSearchParams = new URLSearchParams(search);
      for (const [key, value] of currentSearchParams.entries()) {
        if (["variant", "limit"].indexOf(key) === -1 && value) {
          params.append(key, value);
        }
      }
    }
    const href = `${this.subscribePath}?${params.toString()}`;
    return (
      <div class="stzh-search__results-subscribe">
        <stzh-button variant="secondary" size="small" href={href}>
          {this.subscribeLabel}
        </stzh-button>
      </div>
    );
  }

  render() {
    const filtersMainUsed = hasSlot(this.element, "filters-main");
    const filtersAsideUsed = hasSlot(this.element, "filters-aside");
    let chipsProvided = true;
    if (filtersMainUsed) {
      const providedChipselect = this.element.querySelector("[slot='filters-main'] stzh-chipselect");
      const providedChipgroup = this.element.querySelector("[slot='filters-main'] stzh-chipgroup");
      if (!providedChipselect && !providedChipgroup) {
        chipsProvided = false;
      }
    } else {
      chipsProvided = false;
    }

    const resultsShown = (this.hasRequiredParams || this.firstFetched) && this.firstFetched && this.initiallyFetched;

    const classes = {
      "stzh-search": true,
      "stzh-search--has-filters-main": filtersMainUsed,
      "stzh-search--has-filters-aside": filtersAsideUsed,
      "stzh-search--has-hidden-main-filters": this.hideMainFilters,
      "stzh-search--has-hidden-aside-filters": this.hideAsideFilters,
      "stzh-search--has-results-shown": resultsShown,
      "stzh-search--has-error-shown": !!this.error,
      "stzh-search--is-loading": this.loading,
      [`stzh-search--${this.variant}`]: !!this.variant,
    };

    const WrapperElement =
      typeof this.templateResultWrapperElement === "function"
        ? this.templateResultWrapperElement(this.element)
        : this.templateResultWrapperElement;

    const wrapperProps =
      typeof this.templateResultWrapperProps === "function"
        ? this.templateResultWrapperProps(this.element)
        : this.templateResultWrapperProps;

    return (
      <Host has-aside-filters={filtersAsideUsed} fullwidth={this.isStzhAnchornavVisible.toString()}>
        <form
          ref={el => (this.formElement = el as HTMLFormElement)}
          novalidate
          class={classes}
          onSubmit={this.handleFormSubmit}
          onReset={this.handleFormReset}
        >
          {/* @ts-ignore */}
          <div class="stzh-search__filters-main" onStzhChange={this.handleMainFilterChange}>
            {!!this.heading && (
              <stzh-heading class="stzh-search__header-heading" curve="h2">
                {this.heading}
              </stzh-heading>
            )}
            <slot name="filters-main"></slot>
            {!chipsProvided && this.chipResults.length > 0 && (
              <div class="stzh-search__filters-main-chips">
                {this.chipResultsHeading && (
                  <stzh-text font="heavy" size="milli" color="primary">
                    {this.chipResultsHeading}
                  </stzh-text>
                )}
                {this.chipResults[0].options ? (
                  <stzh-chipgroup>
                    {this.chipResults.map(chipResult => (
                      <stzh-dropdown
                        label={chipResult.label}
                        name={chipResult.value}
                        variant="filter"
                        size="large"
                        no-search={true}
                        hide-optional={true}
                        inline={true}
                        options={chipResult.options}
                      ></stzh-dropdown>
                    ))}
                  </stzh-chipgroup>
                ) : (
                  <stzh-chipselect
                    multiple={true}
                    name="subject"
                    label={this.chipResultsHeading}
                    label-hidden={true}
                    data={this.chipResults.map(chipResult => ({
                      label: chipResult.label,
                      value: chipResult.value,
                      counter: `${chipResult.amount}`,
                      variant: "filter",
                      size: "large",
                    }))}
                  ></stzh-chipselect>
                )}
              </div>
            )}
          </div>

          {filtersAsideUsed && (
            <Fragment>
              {/* @ts-ignore */}
              <div class="stzh-search__filters-aside" onStzhChange={this.handleAsideFilterChange}>
                <div class="stzh-search__filters-aside-inner">
                  <div class="stzh-search__filters-aside-heading">{this.localization.moreFilter}</div>
                  <stzh-details open={this.isDetailsOpen} class="stzh-search__filters-aside-details">
                    <stzh-button
                      class="stzh-search__filters-aside-details-trigger"
                      variant="tertiary-plain"
                      size="small"
                      noPaddingLeft={true}
                      showToggleIcon={true}
                      iconPosition="right"
                      label={this.localization.moreFilter}
                    ></stzh-button>
                    <div slot="content">
                      <slot name="filters-aside"></slot>
                    </div>
                  </stzh-details>
                </div>
              </div>
            </Fragment>
          )}

          <div class="stzh-search__results-wrapper">
            {resultsShown && (
              <Fragment>
                {this.results.length === 0 ? (
                  <Fragment>
                    {this.variant !== "teaser" && this.resultsHeading && !this.hideResultsHeading && (
                      <stzh-heading
                        class="stzh-search__results-heading"
                        curve="h3"
                        level={this.resultHeadingLevel}
                        innerHTML={this.resultsHeading}
                      ></stzh-heading>
                    )}

                    {this.resultsText && this.renderResultsText()}

                    {this.subscribePath && this.renderSubscribeButton()}

                    {this.emptyText && (
                      <stzh-text class="stzh-search__empty-text" innerHTML={this.emptyText}></stzh-text>
                    )}

                    {this.emptyLinkLabel && this.emptyLinkHref && (
                      <div class="stzh-search__empty-link-wrapper">
                        <stzh-link href={this.emptyLinkHref} label={this.emptyLinkLabel}></stzh-link>
                      </div>
                    )}
                  </Fragment>
                ) : (
                  <Fragment>
                    <div class="stzh-search__results-header">
                      {this.variant !== "teaser" && this.resultsHeading && !this.hideResultsHeading && (
                        <stzh-heading
                          class="stzh-search__results-heading"
                          curve="h3"
                          level={this.resultHeadingLevel}
                          htmlContent={this.resultsHeading}
                        ></stzh-heading>
                      )}
                      {this._sortOptions.length > 0 && (
                        <div class="stzh-search__results-sort">
                          <stzh-dropdown
                            hide-optional
                            inline
                            no-search
                            no-wrap
                            class="stzh-search__results-sort-dropdown"
                            variant="plain"
                            size="small"
                            sizeMedium={"tiny"}
                            name="sortby"
                            items={[this._sortBy]}
                            options={this._sortOptions}
                            onStzhChange={this.onDropdownSortChange}
                          ></stzh-dropdown>
                        </div>
                      )}
                    </div>

                    {this.resultsText && this.renderResultsText()}

                    {this.subscribePath && this.renderSubscribeButton()}

                    <WrapperElement class="stzh-search__results" max-3-columns={filtersAsideUsed} {...wrapperProps}>
                      {this.results.map(result => (
                        <div class="stzh-search__results-item" innerHTML={result.html || ""}></div>
                      ))}
                    </WrapperElement>

                    <div class="stzh-search__actions-wrapper">
                      {this._showMoreLink && (
                        <stzh-button
                          variant="secondary"
                          class="stzh-search__action-more"
                          label={this.localization.showMore}
                          onClick={this.handleMoreClick}
                          disabled={this.loading}
                          a11yDisabled={this.loading}
                        ></stzh-button>
                      )}
                    </div>
                  </Fragment>
                )}
              </Fragment>
            )}

            {this.error && (
              <div class="stzh-search__error">
                <stzh-message type="error" label={this.error}></stzh-message>
              </div>
            )}

            {this.loading && <stzh-loader class="stzh-search__loader"></stzh-loader>}
          </div>

          <slot></slot>
        </form>
      </Host>
    );
  }
}
